// What this browser tab remembers about the consult it checked in, so the call
// page can reconnect after a reload and the thank-you page can greet the owner
// by first name. sessionStorage: this tab only, gone when the tab closes.
//
// The live token is a 4 hour bearer credential for this one consult (design
// 3.4 step 2). It lives only here and in memory, never in a URL, a log or
// localStorage. Storage can be blocked (private mode, strict settings); every
// read and write is guarded and the page still works without it, except that a
// reload then cannot rejoin (it says so plainly).
import { z } from "zod";
import { ClientAudioSettings } from "@otto/api";

const TabRecord = z.object({
  consult_id: z.uuid(),
  live_path: z.string().startsWith("/live/"),
  /** Where otto-live answers; null: this page's own origin. */
  live_origin: z.string().nullable().default(null),
  live_token: z.string().min(1),
  live_token_expires_at: z.string(),
  mode: z.enum(["video", "voice"]),
  language: z.enum(["en", "es"]),
  first_name: z.string().max(100),
  business_name: z.string().max(200),
  agenda: z.array(z.object({ id: z.string(), label: z.string() })),
  target_minutes: z.number(),
  cap_minutes: z.number(),
  /** The browser's audio settings pinned at check-in; null: the config defaults. */
  client_audio: ClientAudioSettings.nullable().default(null),
  /** Set once the server said `ready`: a reload then sends `resume`, not `hello`. */
  started: z.boolean().default(false),
  /** Last time the call page saw the socket up (ms since epoch), for resume's gap. */
  last_seen_at: z.number().nullable().default(null),
  /** Last Otto turn this tab finished playing. */
  last_turn_played: z.number().int().nullable().default(null),
});
export type ConsultTabRecord = z.infer<typeof TabRecord>;

const key = (consultId: string) => `otto.consult.${consultId}`;

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function saveConsultRecord(record: z.input<typeof TabRecord>): boolean {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(key(record.consult_id), JSON.stringify(TabRecord.parse(record)));
    return true;
  } catch {
    return false;
  }
}

export function readConsultRecord(consultId: string): ConsultTabRecord | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(key(consultId));
    if (!raw) return null;
    const parsed = TabRecord.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function updateConsultRecord(consultId: string, patch: Partial<ConsultTabRecord>): void {
  const current = readConsultRecord(consultId);
  if (!current) return;
  saveConsultRecord({ ...current, ...patch });
}

/** True when the stored live token has expired (or cannot be read). */
export function liveTokenExpired(record: ConsultTabRecord, now = Date.now()): boolean {
  const expires = Date.parse(record.live_token_expires_at);
  return !Number.isFinite(expires) || expires <= now;
}

export function firstNameOf(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? "";
}
